import { useCallback } from 'react';

import usePersistData from 'modules/common/hooks/usePersistData';

export interface StepperProgress {
    [wizardKey: string]: {
        lastVisitedPath: string;
        timestamp: number;
    };
}

interface UsePersistStepperProgressOptions {
    wizardKey: string;
    enabled?: boolean;
}

/**
 * Hook to persist stepper progress in localStorage with user-specific storage
 *
 * Features:
 * - Stores the last visited path for each wizard under a user-specific "stepper" key
 * - Uses usePersistData to automatically handle user.pk prefixing for multi-user support
 * - Different users on the same device will have separate progress storage
 * - Storage format: `{user.pk}.stepper` containing wizard progress data
 * - **Forward-only progress tracking**: lastVisitedPath represents the furthest step reached,
 *   not the current step (important for back navigation scenarios)
 *
 * @example
 * ```typescript
 * const { saveLastVisited, getLastVisited, clearProgress } = usePersistStepperProgress({
 *   wizardKey: 'business-setup',
 *   enabled: true
 * });
 *
 * // In StepWizard2, this is automatically called only when moving forward
 * saveLastVisited('/step-3'); // User reached step 3
 * // If user goes back to step 2, lastVisitedPath remains '/step-3'
 * // When they return later, they resume at step 3 (furthest point)
 * ```
 */
function usePersistStepperProgress({ wizardKey, enabled = true }: UsePersistStepperProgressOptions) {
    const { getData, setData } = usePersistData<StepperProgress>();

    const saveLastVisited = useCallback(
        (path: string) => {
            if (!enabled) {
                return;
            }

            try {
                const existingData = getData('stepper') || {};

                const updatedData = {
                    ...existingData,
                    [wizardKey]: {
                        lastVisitedPath: path,
                        timestamp: Date.now(),
                    },
                };

                setData('stepper', updatedData);
            } catch (error) {
                console.warn('Failed to save stepper progress:', error);
            }
        },
        [wizardKey, enabled, getData, setData]
    );

    const getLastVisited = useCallback((): string | null => {
        if (!enabled) {
            return null;
        }

        try {
            const stepperData = getData('stepper') || {};
            return stepperData[wizardKey]?.lastVisitedPath || null;
        } catch (error) {
            console.warn('Failed to load stepper progress:', error);
            return null;
        }
    }, [wizardKey, enabled, getData]);

    const clearProgress = useCallback(() => {
        if (!enabled) {
            return;
        }

        try {
            const existingData = getData('stepper') || {};
            const updatedData = { ...existingData };
            delete updatedData[wizardKey];

            setData('stepper', updatedData);
        } catch (error) {
            console.warn('Failed to clear stepper progress:', error);
        }
    }, [wizardKey, enabled, getData, setData]);

    return {
        saveLastVisited,
        getLastVisited,
        clearProgress,
    };
}

export default usePersistStepperProgress;
